import type { SalaryFrequency, WageZone } from "./types";

export interface TaxBracket {
  lower: number;
  upper: number;
  fixed: number;
  rate: number;
}

export interface LegalParameters {
  version: string;
  supportedFrom: string;
  supportedTo: string;
  umaDaily: number;
  umaMonthly: number;
  minimumWage: Record<WageZone, number>;
  overtimeDoubleWeeklyLimit: number;
  overtimeAbsoluteWeeklyLimit: number;
  ordinaryWeeklyHours: number;
  subsidyMonthlyIncomeLimit: number;
  subsidyUmaRate: number;
  subsidyTable?: Array<{ upper: number; amount: number }>;
  monthlyTaxTable: TaxBracket[];
  dailyTaxTable: TaxBracket[];
  ceavEmployerRates: Array<{
    maxUma: number;
    rate: number;
  }>;
}

const MONTHLY_2026: TaxBracket[] = [
  { lower: 0.01, upper: 844.59, fixed: 0, rate: 0.0192 },
  { lower: 844.6, upper: 7_168.51, fixed: 16.22, rate: 0.064 },
  { lower: 7_168.52, upper: 12_598.02, fixed: 420.95, rate: 0.1088 },
  { lower: 12_598.03, upper: 14_644.64, fixed: 1_011.68, rate: 0.16 },
  { lower: 14_644.65, upper: 17_533.64, fixed: 1_339.14, rate: 0.1792 },
  { lower: 17_533.65, upper: 35_362.83, fixed: 1_856.84, rate: 0.2136 },
  { lower: 35_362.84, upper: 55_736.68, fixed: 5_665.16, rate: 0.2352 },
  { lower: 55_736.69, upper: 106_410.5, fixed: 10_457.09, rate: 0.3 },
  { lower: 106_410.51, upper: 141_880.66, fixed: 25_659.23, rate: 0.32 },
  { lower: 141_880.67, upper: 425_641.99, fixed: 37_009.69, rate: 0.34 },
  { lower: 425_642, upper: Number.POSITIVE_INFINITY, fixed: 133_488.54, rate: 0.35 },
];

const MONTHLY_2024_2025: TaxBracket[] = [
  { lower: 0.01, upper: 746.04, fixed: 0, rate: 0.0192 },
  { lower: 746.05, upper: 6_332.05, fixed: 14.32, rate: 0.064 },
  { lower: 6_332.06, upper: 11_128.01, fixed: 371.83, rate: 0.1088 },
  { lower: 11_128.02, upper: 12_935.82, fixed: 893.63, rate: 0.16 },
  { lower: 12_935.83, upper: 15_487.71, fixed: 1_182.88, rate: 0.1792 },
  { lower: 15_487.72, upper: 31_236.49, fixed: 1_640.18, rate: 0.2136 },
  { lower: 31_236.5, upper: 49_233, fixed: 5_004.12, rate: 0.2352 },
  { lower: 49_233.01, upper: 93_993.9, fixed: 9_236.89, rate: 0.3 },
  { lower: 93_993.91, upper: 125_325.2, fixed: 22_665.17, rate: 0.32 },
  { lower: 125_325.21, upper: 375_975.61, fixed: 32_691.18, rate: 0.34 },
  {
    lower: 375_975.62,
    upper: Number.POSITIVE_INFINITY,
    fixed: 117_912.32,
    rate: 0.35,
  },
];

const DAILY_2026: TaxBracket[] = [
  { lower: 0.01, upper: 27.78, fixed: 0, rate: 0.0192 },
  { lower: 27.79, upper: 235.81, fixed: 0.53, rate: 0.064 },
  { lower: 235.82, upper: 414.41, fixed: 13.85, rate: 0.1088 },
  { lower: 414.42, upper: 481.73, fixed: 33.28, rate: 0.16 },
  { lower: 481.74, upper: 576.76, fixed: 44.05, rate: 0.1792 },
  { lower: 576.77, upper: 1_163.25, fixed: 61.08, rate: 0.2136 },
  { lower: 1_163.26, upper: 1_833.44, fixed: 186.35, rate: 0.2352 },
  { lower: 1_833.45, upper: 3_500.35, fixed: 343.98, rate: 0.3 },
  { lower: 3_500.36, upper: 4_667.13, fixed: 844.05, rate: 0.32 },
  { lower: 4_667.14, upper: 14_001.38, fixed: 1_217.42, rate: 0.34 },
  { lower: 14_001.39, upper: Number.POSITIVE_INFINITY, fixed: 4_391.07, rate: 0.35 },
];

const DAILY_2024_2025: TaxBracket[] = [
  { lower: 0.01, upper: 24.54, fixed: 0, rate: 0.0192 },
  { lower: 24.55, upper: 208.29, fixed: 0.47, rate: 0.064 },
  { lower: 208.3, upper: 366.05, fixed: 12.23, rate: 0.1088 },
  { lower: 366.06, upper: 425.52, fixed: 29.4, rate: 0.16 },
  { lower: 425.53, upper: 509.46, fixed: 38.91, rate: 0.1792 },
  { lower: 509.47, upper: 1_027.52, fixed: 53.96, rate: 0.2136 },
  { lower: 1_027.53, upper: 1_619.51, fixed: 164.61, rate: 0.2352 },
  { lower: 1_619.52, upper: 3_091.9, fixed: 303.85, rate: 0.3 },
  { lower: 3_091.91, upper: 4_122.54, fixed: 745.56, rate: 0.32 },
  { lower: 4_122.55, upper: 12_367.62, fixed: 1_075.37, rate: 0.34 },
  {
    lower: 12_367.63,
    upper: Number.POSITIVE_INFINITY,
    fixed: 3_878.69,
    rate: 0.35,
  },
];

const SUBSIDY_TABLE_2024 = [
  { upper: 1_768.96, amount: 407.02 },
  { upper: 2_653.38, amount: 406.83 },
  { upper: 3_472.84, amount: 406.62 },
  { upper: 3_537.87, amount: 392.77 },
  { upper: 4_446.15, amount: 382.46 },
  { upper: 4_717.18, amount: 354.23 },
  { upper: 5_335.42, amount: 324.87 },
  { upper: 6_224.67, amount: 294.63 },
  { upper: 7_113.9, amount: 253.54 },
  { upper: 7_382.33, amount: 217.61 },
  { upper: Number.POSITIVE_INFINITY, amount: 0 },
];

const CEAV_2024 = [
  { maxUma: 1.5, rate: 0.03413 },
  { maxUma: 2, rate: 0.04 },
  { maxUma: 2.5, rate: 0.04353 },
  { maxUma: 3, rate: 0.04588 },
  { maxUma: 3.5, rate: 0.04756 },
  { maxUma: 4, rate: 0.04882 },
  { maxUma: Number.POSITIVE_INFINITY, rate: 0.05331 },
];

const CEAV_2025 = [
  { maxUma: 1.5, rate: 0.03544 },
  { maxUma: 2, rate: 0.04426 },
  { maxUma: 2.5, rate: 0.04954 },
  { maxUma: 3, rate: 0.05307 },
  { maxUma: 3.5, rate: 0.05559 },
  { maxUma: 4, rate: 0.05747 },
  { maxUma: Number.POSITIVE_INFINITY, rate: 0.06422 },
];

const CEAV_2026 = [
  { maxUma: 1.5, rate: 0.03676 },
  { maxUma: 2, rate: 0.04851 },
  { maxUma: 2.5, rate: 0.05556 },
  { maxUma: 3, rate: 0.06026 },
  { maxUma: 3.5, rate: 0.06361 },
  { maxUma: 4, rate: 0.06613 },
  { maxUma: Number.POSITIVE_INFINITY, rate: 0.07513 },
];

const common = {
  supportedFrom: "2024-01-01",
  supportedTo: "2026-12-31",
} satisfies Omit<
  LegalParameters,
  | "version"
  | "umaDaily"
  | "umaMonthly"
  | "minimumWage"
  | "overtimeAbsoluteWeeklyLimit"
  | "overtimeDoubleWeeklyLimit"
  | "ordinaryWeeklyHours"
  | "subsidyMonthlyIncomeLimit"
  | "subsidyUmaRate"
  | "monthlyTaxTable"
  | "dailyTaxTable"
  | "ceavEmployerRates"
>;

function isoDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error("La fecha debe usar el formato AAAA-MM-DD.");
  }
  return value;
}

export function parametersFor(payDate: string): LegalParameters {
  const date = isoDate(payDate);
  if (date < common.supportedFrom || date > common.supportedTo) {
    throw new Error(
      "Esta versión sólo admite fechas de pago entre el 1 de enero de 2024 y el 31 de diciembre de 2026.",
    );
  }

  const year = Number(date.slice(0, 4)) as 2024 | 2025 | 2026;
  const fortyHourReform = date >= "2026-05-01";
  const yearSettings = {
    2024: {
      umaDaily: date < "2024-02-01" ? 103.74 : 108.57,
      umaMonthly: date < "2024-02-01" ? 3_153.7 : 3_300.53,
      minimumWage: { general: 248.93, border: 374.89 },
      ceavEmployerRates: CEAV_2024,
      subsidyMonthlyIncomeLimit:
        date < "2024-05-01" ? 7_382.33 : 9_081,
      subsidyUmaRate: date < "2024-05-01" ? 0 : 0.1182,
      subsidyTable:
        date < "2024-05-01" ? SUBSIDY_TABLE_2024 : undefined,
      monthlyTaxTable: MONTHLY_2024_2025,
      dailyTaxTable: DAILY_2024_2025,
    },
    2025: {
      umaDaily: date < "2025-02-01" ? 108.57 : 113.14,
      umaMonthly: date < "2025-02-01" ? 3_300.53 : 3_439.46,
      minimumWage: { general: 278.8, border: 419.88 },
      ceavEmployerRates: CEAV_2025,
      subsidyMonthlyIncomeLimit: 10_171,
      subsidyUmaRate: date < "2025-02-01" ? 0.1439 : 0.138,
      monthlyTaxTable: MONTHLY_2024_2025,
      dailyTaxTable: DAILY_2024_2025,
    },
    2026: {
      umaDaily: date < "2026-02-01" ? 113.14 : 117.31,
      umaMonthly: date < "2026-02-01" ? 3_439.46 : 3_566.22,
      minimumWage: { general: 315.04, border: 440.87 },
      ceavEmployerRates: CEAV_2026,
      subsidyMonthlyIncomeLimit: 11_492.66,
      subsidyUmaRate: date < "2026-02-01" ? 0.1559 : 0.1502,
      monthlyTaxTable: MONTHLY_2026,
      dailyTaxTable: DAILY_2026,
    },
  }[year];

  return {
    ...common,
    ...yearSettings,
    version: `mx-${year}.${date.slice(0, 7)}.${
      fortyHourReform ? "lft-2026-05" : "lft-pre-2026-05"
    }`,
    overtimeDoubleWeeklyLimit: fortyHourReform ? 12 : 9,
    overtimeAbsoluteWeeklyLimit: fortyHourReform ? 16 : 9,
    ordinaryWeeklyHours: fortyHourReform ? 40 : 48,
  };
}

export const frequencyDays: Record<SalaryFrequency, number> = {
  daily: 1,
  weekly: 7,
  fourteen: 14,
  biweekly: 15,
  monthly: 30,
};

export function statutoryVacationDays(completedYears: number): number {
  const years = Math.max(0, Math.floor(completedYears));
  if (years <= 0) return 12;
  if (years <= 5) return 10 + years * 2;
  return 20 + Math.ceil((years - 5) / 5) * 2;
}

export const LEGAL_SOURCES = {
  lft: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LFT.pdf",
  lss: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LSS.pdf",
  lisr: "https://www.diputados.gob.mx/LeyesBiblio/pdf/LISR.pdf",
  annex8:
    "https://www.sat.gob.mx/minisitio/NormatividadRMFyRGCE/documentos2026/rmf/anexos/Anexo-8-RMF-2026_DOF-28122025.pdf",
  annex8_2025:
    "https://www.sat.gob.mx/minisitio/NormatividadRMFyRGCE/documentos2025/rmf/anexos/Anexo8_RMF2025-30122024.pdf",
  annex8_2024:
    "https://www.sat.gob.mx/minisitio/NormatividadRMFyRGCE/documentos2024/rmf/anexos/Anexo_8_RMF2024-29122023.pdf",
  subsidy:
    "https://www.dof.gob.mx/nota_detalle.php?codigo=5777649&fecha=31/12/2025",
  uma: "https://www.inegi.org.mx/contenidos/saladeprensa/boletines/2026/uma/uma2026.pdf",
  minimumWage:
    "https://www.gob.mx/conasami/articulos/incremento-a-los-salarios-minimos-para-2026",
  sua: "https://www.imss.gob.mx/patrones/sua",
};

export function fiscalSourcesFor(payDate: string): string[] {
  const year = payDate.slice(0, 4);
  const annex =
    year === "2024"
      ? LEGAL_SOURCES.annex8_2024
      : year === "2025"
        ? LEGAL_SOURCES.annex8_2025
        : LEGAL_SOURCES.annex8;
  return [annex, LEGAL_SOURCES.subsidy];
}
